import { ApiError } from "@lumora/bridge";
import { ResetCode } from "../models/ResetCode.js";
import { Session } from "../models/Session.js";
import { ViewModel } from "../mvvm/ViewModel.js";
/**
 * AuthViewModel binds a sign-in View to the Session model via the bridge.
 * Pass site when known (Franks, platform admin). Omit for email-only owner portal (#458).
 */
export class AuthViewModel extends ViewModel {
    client;
    site;
    options;
    constructor(client, site = "", options = {}) {
        super({
            email: "",
            password: "",
            loading: false,
            error: null,
            session: Session.anonymous(),
            mode: "signIn",
            resetCode: "",
            newPassword: "",
            confirmPassword: "",
            lockedSeconds: options.lockout?.remainingSeconds() ?? 0,
        });
        this.client = client;
        this.site = site;
        this.options = options;
    }
    setMode(mode) {
        this.setState({ mode, error: null });
    }
    setResetCode(resetCode) {
        this.setState({ resetCode: ResetCode.normalize(resetCode), error: null });
    }
    setConfirmPassword(confirmPassword) {
        this.setState({ confirmPassword, error: null });
    }
    setNewPassword(newPassword) {
        this.setState({ newPassword, error: null });
    }
    /** Re-reads the lockout clock; Views call this on a 1s tick while locked. */
    refreshLockout() {
        this.setState({ lockedSeconds: this.options.lockout?.remainingSeconds() ?? 0 });
    }
    lockedNow() {
        this.refreshLockout();
        return this.state.lockedSeconds > 0;
    }
    async resetPassword() {
        const port = this.options.resetPort;
        if (!port || this.lockedNow())
            return false;
        const code = ResetCode.parse(this.state.resetCode);
        const minNew = this.options.minNewPasswordLength ?? 8;
        if (!code || this.state.newPassword.length < minNew) {
            this.setState({
                error: `Enter a valid reset code and a password with at least ${minNew} characters.`,
                newPassword: "",
            });
            return false;
        }
        const ok = await this.run(async () => {
            await port.reset({
                identifier: this.options.resolveIdentifier?.(this.state.email) ?? this.state.email,
                resetCode: code.toString(),
                newPassword: this.state.newPassword,
            });
            return true;
        }, "Password reset failed. Please try again.");
        if (ok !== true)
            return false;
        this.setState({ mode: "signIn", resetCode: "", newPassword: "", password: "" });
        return true;
    }
    setEmail(email) {
        this.setState({ email, error: null });
    }
    setPassword(password) {
        this.setState({ password, error: null });
    }
    async signIn() {
        if (this.lockedNow())
            return false;
        const minId = this.options.minIdentifierLength ?? 0;
        const minPw = this.options.minPasswordLength ?? 0;
        if (this.state.email.length < minId || this.state.password.length < minPw) {
            this.setState({
                error: this.options.messages?.credentialsHint ??
                    `Enter your sign-in name and a password of ${minPw}+ characters.`,
                password: "",
            });
            return false;
        }
        const ok = await this.run(async () => {
            const tokens = await this.client.login({
                ...(this.site ? { site: this.site } : {}),
                email: this.options.resolveIdentifier?.(this.state.email) ?? this.state.email,
                password: this.state.password,
            });
            if (tokens.mustChangePassword) {
                // Temporary credential: no session until the password is replaced (#1077).
                // The typed password stays in state as the "current" password for the change.
                this.setState({
                    mode: "changePassword",
                    newPassword: "",
                    confirmPassword: "",
                    error: "Your password is temporary. Choose a new one to continue.",
                });
                return "mustChange";
            }
            this.openSession(tokens);
            return true;
        }, "Sign-in failed. Please try again.", {
            onError: (err) => {
                if (err instanceof ApiError && (err.status === 429 || err.code === "IP_BLOCKED")) {
                    this.options.lockout?.trigger();
                    return { password: "", lockedSeconds: this.options.lockout?.remainingSeconds() ?? 0 };
                }
                if (err instanceof ApiError && err.status === 403 && err.code === "RESET_REQUIRED") {
                    return { mode: "reset", password: "" };
                }
                const failed = this.options.messages?.signInFailed;
                return {
                    ...(this.options.minPasswordLength ? { password: "" } : {}),
                    ...(failed ? { error: failed } : {}),
                };
            },
        });
        return ok === true;
    }
    openSession(tokens) {
        const claims = this.client.claims();
        this.setState({
            session: new Session(tokens, claims?.role ?? null, claims?.sub ?? null),
        });
        if (tokens.site && typeof localStorage !== "undefined") {
            localStorage.setItem("lumora.lastSiteSlug", tokens.site);
        }
    }
    /**
     * Second step of a temporary-credential sign-in: replaces the password (the typed one is
     * the current password) and only then opens the session. Validated before any request.
     */
    async changePassword() {
        const { password, newPassword, confirmPassword } = this.state;
        const minNew = this.options.minNewPasswordLength ?? 12;
        let problem = null;
        if (newPassword.length < minNew)
            problem = `Use at least ${minNew} characters for your new password.`;
        else if (newPassword !== confirmPassword)
            problem = "The two new passwords do not match.";
        else if (newPassword === password)
            problem = "Choose a password different from the temporary one.";
        if (problem) {
            this.setState({ error: problem, newPassword: "", confirmPassword: "" });
            return false;
        }
        const ok = await this.run(async () => {
            const pair = await this.client.changePassword({ current: password, new: newPassword });
            this.openSession({ ...pair, site: this.site || undefined });
            this.setState({ mode: "signIn", password: "", newPassword: "", confirmPassword: "" });
            return true;
        }, "Could not change the password. Please try again.", {
            onError: () => ({ newPassword: "", confirmPassword: "" }),
        });
        return ok === true;
    }
    /** Registers then signs in. Role defaults to owner; pass admin for the admin portal. */
    async signUp(role = "owner") {
        const registered = await this.run(async () => {
            await this.client.register({
                site: this.site,
                email: this.state.email,
                password: this.state.password,
                role,
            });
            return true;
        }, "Sign-up failed. Please try again.");
        if (registered !== true)
            return false;
        return this.signIn();
    }
    /** Consumes invite token + password; stores session JWT (Closes #298). */
    async acceptOwnerInvite(token) {
        const ok = await this.run(async () => {
            const tokens = await this.client.acceptOwnerInvite({
                token,
                password: this.state.password,
            });
            const claims = this.client.claims();
            this.setState({
                session: new Session(tokens, claims?.role ?? null, claims?.sub ?? null),
            });
            return true;
        }, "Invite accept failed. Please try again.");
        return ok === true;
    }
    /** Opens Stripe Connect hosted onboarding for this site (owner JWT). */
    async startPayoutOnboarding(returnUrl, refreshUrl) {
        const res = await this.run(() => this.client.startOwnerConnectOnboarding(this.site, returnUrl, refreshUrl), "Could not start payout setup.");
        return res?.url ?? null;
    }
    signOut() {
        this.client.logout();
        this.setState({ session: Session.anonymous(), email: "", password: "" });
    }
}
//# sourceMappingURL=AuthViewModel.js.map